import { fetchAvailableDrafts, getCpiValuesList } from '@/api/property-management.api';
import { fetchRentalContracts, findRentingLeads, getRentingListing, removeRentingLead } from '@/api/renting.api';
import type { MaybeRef } from '@/shared/typings';
import { RentIncreaseType, type RentingListing } from '@condo/domain';
import type {
    FindAvailableDraftsForRentalContractQueryParams,
    FindRentalContractsQueryParams,
    FindRentingLeadsQueryParams,
    GetCpiValuesListQueryResponse,
    GetRentingListingQueryParams,
} from '@condo/sdk/propx';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, unref } from 'vue';

export const useRentalContracts = (paramsOrRef: MaybeRef<FindRentalContractsQueryParams>) => {
    const params = computed(() => unref(paramsOrRef));
    return useQuery(['rental-contracts', params], () => fetchRentalContracts(params.value), {
        initialData: {
            rentalContracts: [],
            total: 0,
        },
    });
};

export const useFindRentingLeads = (paramsOrRef: MaybeRef<FindRentingLeadsQueryParams>) => {
    return useQuery(['renting-leads', paramsOrRef], () => findRentingLeads(unref(paramsOrRef)), { initialData: [] });
};

/**
 * @deprecated moved to RAM
 */
export const useAvailableRentalContractDrafts = (paramsOrRef: MaybeRef<FindAvailableDraftsForRentalContractQueryParams>) => {
    return useQuery(['available-rental-contract-drafts', paramsOrRef], () => fetchAvailableDrafts(unref(paramsOrRef)), { initialData: [] });
};

export const useRemoveRentingLead = () => {
    const queryClient = useQueryClient();
    return useMutation<void, number, number>(async rentingLeadId => {
        await removeRentingLead(rentingLeadId);
        queryClient.invalidateQueries({ queryKey: ['renting-leads'] });
    });
};

export const useCpiValuesList = (params: MaybeRef<{ rentIncreaseType?: RentIncreaseType; startDate?: Date; endDate?: Date }>) => {
    return useQuery<GetCpiValuesListQueryResponse>(['cpi-values-list', params], () => getCpiValuesList(unref(params).startDate, unref(params).endDate), {
        initialData: [],
        enabled: computed(() => unref(params).rentIncreaseType === RentIncreaseType.IRI),
    });
};

export const useGetRentingListing = (params: MaybeRef<GetRentingListingQueryParams>) => {
    return useQuery<RentingListing | null>(['renting-listing', params], () => getRentingListing(unref(params)));
};
